// Third-party
import React from 'react';

// Assets
import EmptyContacts from '@/images/design-system-v2/empty-state/emptyContacts.png';

// Libraries
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

// Components
import { Button } from '@capintel/component-lib/button';
import { Tooltip } from '@capintel/component-lib/tooltip';
import { ContactsTable } from '../../components/tables';
import { EditContactModal, DeleteContactsModal } from '../../components/modals';
import { LoadingSpinner } from '../../components/common';
import ConnectedPrivateComponent from '../../components/PrivateComponent';
import ContactsPageNoData from './ContactsPageNoData';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import RateLimitedComponent from '../../components/RateLimitedComponent';
import SearchInput from '../../components/inputs/SearchInput';
import TablePageLayout from '../layouts/TablePageLayout';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Legacy Component
import LegacyContacts from './LegacyContacts';

// Actions
import { getMyContacts, deleteContacts, loadContactForEdit, resetEditContact, setPersistenceSource, GET_CONTACTS } from '../../../redux/modules/contacts';
import { incrementLimit, consumeLimit } from '../../../redux/modules/rate-limits';
import { RateLimitType } from '../../../redux/modules/types/rate-limits';
import { ReduxState } from '../../../redux/modules/types';
import { selectIsLoadingEvery, startLoaderAction } from '../../../redux/modules/ui/loaders';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { getContactLayout, CONTACT_LAYOUT } from '../../../utils/contacts';
import { getThemeVersion } from '@/utils/theme';
import { NAV_PATHS } from '../../../utils/constants';
import { PERMISSIONS } from '../../../utils/permissions';
import { showError } from '../../../utils/errors';
import localStorageUtils from '@/utils/local-storage';
import notification from '../../../utils/notification';
import sessionStorage, { SessionStorageKey } from '../../../utils/session-storage';
import { getTableSortedColumns } from '@/utils/data-table';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

// Types
import Activity from '@/types/activity';
import AppMode from '@/types/app-mode';
import Comparison from '@/types/comparison';
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Portfolio from '@/types/portfolio';
import User from '@/types/user';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import { ColumnSort } from '@tanstack/react-table';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { SortingField } from '../../../types/data';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  deleteContacts: (contacts: GenericContact[]) => Promise<void>;
  loadContactForEdit: (contact: GenericContact) => void;
  resetEditContact: () => void;
  incrementLimit: (limitType: RateLimitType, count?: number) => void;
  consumeLimit: (limitType: RateLimitType) => void;
  startLoaderAction: (name: string) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
};

type StateProps = {
  user?: User | null;
  locale: string;
  editedContact: Partial<GenericContact>;
  questionnaires: KycQuestionnaire[];
  currentQuestionnaire?: KycQuestionnaire;
  contacts: GenericContact[];
  failedToFetchContacts: boolean;
  nextContactsToken?: string;
  loading: {
    contacts: boolean;
    current: {
      portfolios: boolean;
      comparisons: boolean;
      activityLogs: boolean;
    };
  };
  portfolio: Portfolio;
  current?: {
    contact: GenericContact;
    activityLogs?: Activity[];
    portfolios?: Portfolio[];
    nextContactPortfoliosToken?: string;
    nextContactComparisonsToken?: string;
    comparisons?: Comparison[];
    riskProfiles?: ContactRiskProfile[];
    hasMore: {
      comparisons: boolean;
      portfolios: boolean;
    };
    nextActivityLogToken?: string;
  };
  isPostMessageActive: boolean;
  persistenceSource?: string;
};

type Props = RouteComponentProps & DispatchProps & StateProps;

type State = {
  activeDataSource: string;
  redirect?: string;
  searchQuery?: string;
  showEditContactModal: boolean;
  showDeleteContactsModal: boolean;
  selectedContactsForDelete: GenericContact[];
  contactsSortingFields: SortingField[];
  isNewContact?: boolean;
  sortedColumns: ColumnSort[];
};

class ContactsV2 extends React.Component<Props, State> {
  private contactsTable = React.createRef<typeof ContactsTable>();

  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.CONTACTS,
    this.props.isPostMessageActive,
  );

  readonly state: State = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    searchQuery: '',
    selectedContactsForDelete: [],
    showEditContactModal: false,
    showDeleteContactsModal: false,
    contactsSortingFields: [],
    isNewContact: false,
    sortedColumns: [],
  };

  debouncedSearchContacts = debounce(() => {
    const { searchQuery, contactsSortingFields } = this.state;
    this.props.getMyContacts({ query: searchQuery, nextToken: undefined, sort: contactsSortingFields });
  }, SEARCH_DEBOUNCE_TIME);

  componentDidMount() {
    const { user, getMyContacts, isPostMessageActive, setIsActiveContactPostMessage } = this.props;

    if (user?.appConfig.mode === AppMode.CONTACT) {
      const contactId = sessionStorage.getItem(SessionStorageKey.CONTACT_ID);
      if (contactId != null) {
        const redirect = NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contactId);
        this.props.history.push(redirect);
      }
    }

    if (user) {
      // Reset the Persistence Source when the component mounts
      const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
      const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

      if (isInitialDataSourceTabPostMessage) {
        setIsActiveContactPostMessage(false);
      }
      resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, user);
    }

    getMyContacts();
  }

  showDeleteContactsModal = (contacts: GenericContact[]) => {
    this.setState({
      showDeleteContactsModal: true,
      selectedContactsForDelete: contacts,
    });
  };

  onContactsSearchChange = (searchQuery: string) => {
    this.setState({ searchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);
    this.debouncedSearchContacts();
  };

  onAddContact = () => {
    this.props.resetEditContact();
    this.setState({ isNewContact: true });
    this.showEditContactModal();
  };

  editContact = (contact: GenericContact) => {
    this.props.loadContactForEdit(contact);

    if (this.state.isNewContact) {
      this.setState({ isNewContact: false });
    }

    this.showEditContactModal();
  };

  onDeleteContacts = () => {
    const { selectedContactsForDelete } = this.state;
    this.props
      .deleteContacts(selectedContactsForDelete)
      .then(() => {
        this.props.incrementLimit(RateLimitType.CONTACTS, selectedContactsForDelete.length);
        notification.success(formatMessage(`Contacts.DeleteModal.${selectedContactsForDelete.length > 1 ? 'DeleteManySuccess' : 'DeleteOneSuccess'}`));
      })
      .catch(showError);

    this.setState({ selectedContactsForDelete: [] });
    this.hideDeleteContactsModal();
  };

  showEditContactModal = () => {
    this.setState({ showEditContactModal: true });
  };

  hideEditContactModal = () => {
    this.setState({ showEditContactModal: false });
  };

  hideDeleteContactsModal = () => {
    this.setState({ showDeleteContactsModal: false });
  };

  onContactSave = (contact: GenericContact) => {
    this.hideEditContactModal();

    if (this.state.isNewContact) {
      this.props.consumeLimit(RateLimitType.CONTACTS);
      this.redirectToContactPage(contact);
    }
  };

  redirectToContactPage = (contact: GenericContact) => {
    if (contact._id) {
      const redirect = NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contact._id);

      this.props.history.push(redirect);
    }
  };

  onContactClick = (contact: GenericContact) => {
    this.redirectToContactPage(contact);
  };

  onContactsSortingChange = (contactsSortingFields: SortingField[]) => {
    const { searchQuery } = this.state;

    this.setState({ contactsSortingFields });
    this.props.getMyContacts({ query: searchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  handleSorting = (sort: ColumnSort) => {
    const { searchQuery } = this.state;
    const { serverSortingParams, columnSort } = getTableSortedColumns(this.state.contactsSortingFields, sort);
    this.setState({ contactsSortingFields: serverSortingParams, sortedColumns: columnSort });
    this.props.getMyContacts({ query: searchQuery, nextToken: undefined, sort: serverSortingParams });
  };

  onLoadMoreContacts = () => {
    const { searchQuery, contactsSortingFields } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: searchQuery, nextToken: nextContactsToken, sort: contactsSortingFields });
  };

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, getMyContacts, setIsActiveContactPostMessage, setPersistenceSource } = this.props;
    const { searchQuery, contactsSortingFields } = this.state;

    if (!user || !this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    getMyContacts({ query: searchQuery, nextToken: undefined, sort: contactsSortingFields });
  };

  renderSearchInput = () => {
    const { user } = this.props;
    const { searchQuery } = this.state;
    const layout = getContactLayout(user);
    const searchPlaceholder =
      layout === CONTACT_LAYOUT.CLIENT ? formatMessage('Contacts.Search.PlaceholderClient') : formatMessage('Contacts.Search.Placeholder');
    return (
      <>
        {(this.props.contacts.length || this.props.failedToFetchContacts || this.state.searchQuery) && (
          <SearchInput
            className="contacts-search-input search-input-v2"
            onChange={this.onContactsSearchChange}
            placeholder={searchPlaceholder}
            value={searchQuery}
            data-private
            data-dd-privacy="mask"
          />
        )}
      </>
    );
  };

  renderCreateContactButton = (show = true, showIcon = true) => {
    const { user } = this.props;
    const layout = getContactLayout(user);
    const addContactText = layout === CONTACT_LAYOUT.CLIENT ? 'Contacts.AddClient' : 'Contacts.AddContact';
    const hasPersistenceKeySource = user
      ? localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null
      : false;

    return (
      <>
        {show && (
          <ConnectedPrivateComponent forcedFalse={hasPersistenceKeySource} permissions={[PERMISSIONS.CONTACTS_CREATE]}>
            {() => (
              <RateLimitedComponent type={RateLimitType.CONTACTS}>
                {({ disabled, error }) => (
                  <Tooltip position="above" content={error}>
                    <Button
                      variant="primary"
                      icon={showIcon ? 'add' : undefined}
                      disabled={disabled || error}
                      onClick={this.onAddContact}
                      data-testid="contacts-primaryButton-addContact"
                    >
                      <FormattedMessage id={addContactText} />
                    </Button>
                  </Tooltip>
                )}
              </RateLimitedComponent>
            )}
          </ConnectedPrivateComponent>
        )}
      </>
    );
  };

  renderEmptyState = () => {
    const i18nEmptyStatePath = 'EmptyStateCard.Contacts';
    const i18nFailedToFetchContactsPath = 'EmptyStateCard.FailedToFetchContacts';
    return (
      <>
        {this.props.failedToFetchContacts ? (
          <EmptyStateCard
            customContentElement={this.renderCreateContactButton(true, false)}
            imageAlt="Contacts example image"
            imagePath={EmptyContacts}
            messagePath={`${i18nFailedToFetchContactsPath}.Message`}
            titlePath={`${i18nFailedToFetchContactsPath}.Title`}
          />
        ) : !this.props.contacts.length && !this.state.searchQuery ? (
          <EmptyStateCard
            customContentElement={this.renderCreateContactButton(true, false)}
            imageAlt="Contacts example image"
            imagePath={EmptyContacts}
            messagePath={`${i18nEmptyStatePath}.Message`}
            titlePath={`${i18nEmptyStatePath}.Title`}
          />
        ) : (
          <ContactsPageNoData searchQuery={this.state.searchQuery} onAddContact={this.onAddContact} />
        )}
      </>
    );
  };

  render() {
    const { loading, user, contacts, editedContact, persistenceSource, isPostMessageActive, setIsActiveContactPostMessage, setPersistenceSource } = this.props;
    const { activeDataSource, redirect, searchQuery, selectedContactsForDelete, showDeleteContactsModal, showEditContactModal } = this.state;

    if (redirect) {
      return <Redirect push to={redirect} />;
    }

    if (!user) {
      return null;
    }

    const layout = getContactLayout(user);

    const pageTitle = layout === CONTACT_LAYOUT.CLIENT ? 'Contacts.PageTitleClient' : 'Contacts.PageTitle';
    const subPageTitle = layout === CONTACT_LAYOUT.CLIENT ? 'Contacts.SubPageTitleClient' : 'Contacts.SubPageTitle';

    return (
      <>
        <TablePageLayout
          mainClassName="contacts-v2"
          pageTitle={pageTitle}
          subPageTitle={subPageTitle}
          searchInputElement={this.renderSearchInput()}
          pageActionsElement={this.renderCreateContactButton(contacts?.length !== 0 || !!searchQuery)}
          tabsElement={
            <>
              {this.dataSourceTabs && (
                <div className="mt-3 me-5">
                  <DataSourceTabs disabled={loading.contacts} tabs={this.dataSourceTabs} value={activeDataSource} onChange={this.onSelectDataSource} />
                </div>
              )}
            </>
          }
          loadingElement={
            <div className="loading-spinner-container">
              <LoadingSpinner message={formatMessage('Contacts.Loading')} />
            </div>
          }
          noDataElement={this.renderEmptyState()}
          tableElement={
            <ContactsTable
              ref={this.contactsTable}
              multiselect={false}
              selectable={false}
              searchable={false}
              contacts={contacts}
              className={classNames({ hidden: loading.contacts, 'wide-first-column': true })}
              onClick={this.onContactClick}
              onDelete={this.showDeleteContactsModal}
              onEdit={this.editContact}
              user={user}
              layout={layout}
              onSortingChange={this.onContactsSortingChange}
              onLoadMore={this.onLoadMoreContacts}
              debouncedSearchContacts={this.debouncedSearchContacts}
              hasMore={this.props.nextContactsToken != null}
              tableScrollAncestor=".app-scrollbar-container"
              persistenceSource={persistenceSource}
              showDataSourceTabs={false}
              serverSideSorting
              onServerSideSorting={this.handleSorting}
              sortedColumns={this.state.sortedColumns}
              isPostMessageActive={isPostMessageActive}
              setPersistenceSource={setPersistenceSource}
              setIsActiveContactPostMessage={setIsActiveContactPostMessage}
            />
          }
          isLoading={loading.contacts}
          hasData={contacts.length > 0}
        />

        <EditContactModal
          user={user}
          show={showEditContactModal}
          onCancel={this.hideEditContactModal}
          onSave={this.onContactSave}
          contact={editedContact}
          isEditing
        />

        <DeleteContactsModal
          contacts={selectedContactsForDelete}
          show={showDeleteContactsModal}
          onCancel={this.hideDeleteContactsModal}
          onConfirm={this.onDeleteContacts}
        />
      </>
    );
  }
}

class Contacts extends React.Component<Props, State> {
  render() {
    const { isLightVersion } = getThemeVersion(this.props.user);
    if (isLightVersion) {
      return <ContactsV2 {...this.props} />;
    }
    return <LegacyContacts {...this.props} />;
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contacts: state.contacts.contacts,
  failedToFetchContacts: state.contacts.failedToFetchContacts,
  persistenceSource: state.contacts.persistenceSource,
  nextContactsToken: state.contacts.nextContactsToken,
  loading: {
    ...state.contacts.loading,
    contacts: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
  },
  current: state.contacts.current,
  locale: state.intl.locale,
  questionnaires: state.kycQuestionnaire.questionnaires,
  currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
  user: state.auth.user,
  portfolio: state.editPortfolio.portfolio,
  editedContact: state.contacts.editContact,
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default withRouter(
  connect(mapStateToProps, {
    getMyContacts,
    deleteContacts,
    loadContactForEdit,
    resetEditContact,
    incrementLimit,
    consumeLimit,
    startLoaderAction,
    setIsActiveContactPostMessage,
    setPersistenceSource,
  })(Contacts),
);
